<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="element-box">
        <CoachingOverviewData :coaching="coachingData"/>

        <Menu :coaching="coachingData"
              :currentUser="piniaCurrentUser"/>

        <form @submit.prevent="submitForm" novalidate class="mt-4">
          <div class="row">
            <div class="col-lg-6 col-md-9 col-sm-12">
              <p class="mt-4">
                Maßnahme vollumfänglich abgeschlossen
                <span style="color: red;"> *</span>
                <span v-if="v$?.measureFullyCompleted?.$error" style="color: red; padding-left: 10px;">
                  {{ v$?.measureFullyCompleted?.required?.$message }}
                </span>
              </p>
              <div class="row">
                <BaseRadio
                    v-model="formData.measureFullyCompleted"
                    label="Ja"
                    name="measureFullyCompleted"
                    id="1"
                    :labelPosition="'right'"
                    classFormGroup="col-auto"
                    @change="onMeasureCompletedChange"/>

                <BaseRadio
                    v-model="formData.measureFullyCompleted"
                    label="Nein"
                    name="measureFullyCompleted"
                    id="0"
                    :labelPosition="'right'"
                    classFormGroup="col-auto"
                    @change="onMeasureCompletedChange"/>
              </div>
              <div class="row" v-if="showDropdown">
                <div class="col-8" v-if="formData.measureFullyCompleted === '0'">
                  <SelectVue
                      v-model="formData.reasonForMeasureTermination"
                      :options="reasonForMeasureTerminationDropdown"
                      :is-clear="true"
                      placeholder="Wählen Sie ..."
                      :validation="v$?.reasonForMeasureTermination"
                      :messages="v$?.reasonForMeasureTermination?.required?.$message"/>
                </div>
                <div class="col-8" v-else-if="formData.measureFullyCompleted === '1'">
                  <SelectVue
                      v-model="formData.reasonForMeasureTermination"
                      :options="measureFullyCompletedDropdown"
                      :is-clear="true"
                      placeholder="Wählen Sie ..."/>
                </div>
                <div class="col-4" v-if="formData.measureFullyCompleted === '0'">
                  <Datepicker
                      v-model="formData.terminatedAt"
                      placeholder="Abbruchdatum"
                      position="down"
                      :validation="v$?.terminatedAt"
                      :messages="v$?.terminatedAt?.required?.$message"/>
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-3" v-if="operationType !== 'modular_integration_coaching'">
            <div class="col-lg-12">
              <TextEditor
                  v-model="formData.targetControl"
                  @update:model-value="formData.targetControl = $event"
                  :inputStyle="{ height: '200px' }"
                  label="Zielkontrolle"/>
            </div>
            <div class="col-lg-12 mt-4">
              <TextEditor
                  v-model="formData.recommendedAction"
                  @update:model-value="formData.recommendedAction = $event"
                  :inputStyle="{ height: '200px' }"
                  label="Handlungsempfehlung"/>
            </div>
          </div>
          <div class="form-buttons-w text-center">
            <SaveButton
                :label="$t('general.button.save')"
                :loading="isSubmitting"/>
          </div>
        </form>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup>
import portalPrivateCoachingService from '@/api/Portal/PrivateCoaching';
import unemployedPrivateCoachingService from '@/api/Unemployed/PrivateCoaching';
import BaseRadio from '@/components/base/BaseRadio.vue';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import TextEditor from '@/components/TextEditor.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {requiredRule} from '@/shared/validators/validationRules';
import {piniaUserStore} from '@/store/piniaUserStore';
import CoachingOverviewData from '@/views/App/Portal/PrivateCoaching/CoachingOverviewData.vue';
import Menu from '@/views/App/Portal/PrivateCoaching/Menu.vue';
import useVuelidate from '@vuelidate/core';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import SaveButton from '@/components/button/SaveButton.vue';

const piniaUserState = piniaUserStore();
const piniaCurrentUser = computed(() => piniaUserState?.currentUser?.user);
const {t} = useI18n();
const coachingData = ref(null);
const finalInterviewData = ref(null);
const operationType = ref('');
const isDigitalSignature = ref(false);
const isSubmitting = ref(false);
const isLoaded = ref(false);
const isNewLogic = ref(false);
const route = useRoute();
const coachingId = +route.params.id;
const showDropdown = ref(true);

const formData = ref({
  targetControl: '',
  recommendedAction: '',
  measureFullyCompleted: null,
  reasonForMeasureTermination: '',
  terminatedAt: null,
});

const rules = ref({
  measureFullyCompleted: requiredRule(t),
  reasonForMeasureTermination: '',
  terminatedAt: '',
});

const computedRules = computed(() => rules.value);

const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    // send a copy so the radio value in formData stays a string ('0'/'1') if the request fails
    const payload = {
      ...formData.value,
      coachingId: +coachingId,
      measureFullyCompleted: +formData.value.measureFullyCompleted,
    };

    if (finalInterviewData.value?.id) {
      await portalPrivateCoachingService.putFinalInterview(payload, +finalInterviewData.value.id);
    } else {
      await portalPrivateCoachingService.postFinalInterview(payload);
    }

    await getFinalInterviewData();
    isSubmitting.value = false;
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getPrivateCoachingData() {
  try {
    CloseToast();
    const {data} = await unemployedPrivateCoachingService.getPrivateCoaching(+coachingId);

    coachingData.value = data.data;
    operationType.value = data?.data?.nameOfCoaching?.operationType;
    isDigitalSignature.value = data.data.digitalSignature;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getFinalInterviewData() {
  try {
    const {data} = await portalPrivateCoachingService.getFinalInterviewData(+coachingId);
    finalInterviewData.value = data?.data;

    if (finalInterviewData.value) {
      const measureFullyCompleted = data?.data?.measureFullyCompleted ?? null;

      formData.value = {
        ...data.data,
        measureFullyCompleted: measureFullyCompleted === null ? null : String(measureFullyCompleted),
      };

      applyConditionalRules();
    }
  } catch (error) {
    await ErrorToast(error);
  }
}

function onMeasureCompletedChange() {
  formData.value.reasonForMeasureTermination = '';
  formData.value.terminatedAt = null;

  showDropdown.value = false;

  setTimeout(() => {
    showDropdown.value = true;
  }, 100);

  applyConditionalRules();
}

function applyConditionalRules() {
  const isTerminated = formData.value.measureFullyCompleted === '0';

  rules.value = {
    ...rules.value,
    reasonForMeasureTermination: isTerminated ? requiredRule(t) : '',
    terminatedAt: isTerminated ? requiredRule(t) : '',
  };
}

async function checkCoachingStartDateForNewLogic() {
  // coaching failed to load (e.g. 404) – the error toast is already shown
  if (!coachingData.value?.startDate) return;

  const [day, month, year] = coachingData.value.startDate.split('.'); // e.g., '2024-10-06'
  const coachingStartDate = `${year}-${month}-${day}`;
  const newLogicStartFrom = '2024-10-07'; // Target date in ISO format IMPORTANT !!!!!!!!

  operationType.value = coachingData?.value.nameOfCoaching.operationType;

  if (coachingStartDate >= newLogicStartFrom) {
    isNewLogic.value = true;
  }
}

const reasonForMeasureTerminationDropdown = [
  {
    value: 'Krankheit',
    label: 'Krankheit',
  },
  {
    value: 'Integration',
    label: 'Integration',
  },
  {
    value: 'Med. Anbindung',
    label: 'Med. Anbindung',
  },
  {
    value: 'TN nicht mehr erreichbar',
    label: 'TN nicht mehr erreichbar',
  },
  {
    value: 'verstorben',
    label: 'verstorben',
  },
  {
    value: 'Abbruch durch TN',
    label: 'Abbruch durch TN',
  },
  {
    value: 'Abbruch durch Kostenträger',
    label: 'Abbruch durch Kostenträger',
  },
];

const measureFullyCompletedDropdown = [
  {
    value: 'Integration',
    label: 'Integration',
  },
];

onMounted(async () => {
  await Promise.all([
    getPrivateCoachingData(),
    getFinalInterviewData(),
  ]);

  // this must be at the end !!!
  await checkCoachingStartDateForNewLogic(coachingId);

  isLoaded.value = true;
});

</script>

<style scoped>
</style>